import { Checkbox as CheckboxPrimitive, Label as LabelPrimitive, Switch as SwitchPrimitive } from "radix-ui";
import { Check, ChevronRight } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";

// Control boundaries use border.control (>= 3:1, ui-spec §1.3); inputs take the density's control height, 13px text.
export function Input({ className, type = "text", ...props }: React.ComponentProps<"input">) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        "flex h-[var(--nd-control-height)] w-full min-w-0 rounded-md border border-line-control bg-canvas px-2.5 text-ui text-fg-body placeholder:text-fg-muted disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-danger-dot",
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: React.ComponentProps<"textarea">) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(
        "flex min-h-20 w-full rounded-md border border-line-control bg-canvas px-3 py-2 text-body text-fg-body placeholder:text-fg-muted disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

export function Label({ className, ...props }: React.ComponentProps<typeof LabelPrimitive.Root>) {
  return (
    <LabelPrimitive.Root
      data-slot="label"
      className={cn("text-small font-medium text-fg-secondary select-none", className)}
      {...props}
    />
  );
}

export function Checkbox({ className, ...props }: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        "peer size-4 shrink-0 rounded-sm border border-line-control bg-canvas data-[state=checked]:border-primary-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center">
        <Check className="size-3.5" aria-hidden />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

/** A labelled field with an inline error linked by aria-describedby (ui-spec §1.3 Forms). */
export function Field({
  id,
  label,
  hint,
  error,
  required,
  children,
}: {
  id: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: string | undefined;
  required?: boolean;
  children: React.ReactElement<{ id?: string; "aria-describedby"?: string; "aria-invalid"?: boolean }>;
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className="nd-field grid content-start gap-1.5">
      <Label htmlFor={id}>
        {label}
        {required ? <span className="text-fg-muted"> (required)</span> : null}
      </Label>
      {React.cloneElement(children, { id, "aria-describedby": describedBy, "aria-invalid": Boolean(error) })}
      {hint ? (
        <p id={hintId} className="text-small text-fg-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-small text-danger-fg">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Optional form fields, collapsed by default so a create form shows only what matters (ui-spec §3.5). */
export function Advanced({ children, label = "Advanced", defaultOpen = false }: { children: React.ReactNode; label?: string; defaultOpen?: boolean }) {
  return (
    <details className="group" open={defaultOpen}>
      <summary className="flex w-fit cursor-pointer list-none items-center gap-1 text-small text-fg-muted select-none hover:text-fg-primary [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" aria-hidden />
        {label}
      </summary>
      <div className="mt-3 grid gap-4">{children}</div>
    </details>
  );
}

/** An accessible on/off control using the shared Radix primitive and Nodus tokens. */
export function Switch(props: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return <SwitchPrimitive.Root {...props} className={cn("inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-line-control bg-sunken p-0.5 data-[state=checked]:bg-primary disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg-primary", props.className)}>
    <SwitchPrimitive.Thumb className="block size-4 rounded-full bg-fg-primary transition-transform data-[state=checked]:translate-x-5 data-[state=checked]:bg-primary-foreground" />
  </SwitchPrimitive.Root>;
}
